<script lang="ts" setup>
import type { Project } from '@factoryfixinc/ats-interfaces';

import type { ProjectDescriptionJob } from './project-description-screening.utils';
import { getProjectDescriptionScreeningDetails } from './project-description-screening.utils';
import { computed } from 'vue';

const props = defineProps<{
  job: ProjectDescriptionJob;
  project?: Project;
}>();

const screeningDetails = computed(() =>
  getProjectDescriptionScreeningDetails(props.job, props.project),
);
const isOrderedQuestionList = computed(() => screeningDetails.value.questionListType === 'ordered');
const hasQuestions = computed(() =>
  Boolean(screeningDetails.value.questionListType && screeningDetails.value.questions?.length),
);
const questionListTag = computed(() => (isOrderedQuestionList.value ? 'ol' : 'ul'));
const questionListTestId = computed(() =>
  isOrderedQuestionList.value
    ? 'project-screening-ordered-questions'
    : 'project-screening-unordered-questions',
);
</script>

<template>
  <div class="mt-4 border-t border-tint-60 pt-5" data-test-id="project-screening-details">
    <p class="text-[11px] font-bold uppercase tracking-[0.14em] text-shade-800">Screening style</p>
    <h2 class="mt-1 text-sm font-bold leading-5 text-shade-900">
      {{ screeningDetails.title }}
    </h2>
    <p class="mt-1 text-xs leading-5 text-shade-900">
      {{ screeningDetails.summary }}
    </p>

    <component
      :is="questionListTag"
      v-if="hasQuestions"
      class="mt-4 space-y-2 text-xs leading-5 text-shade-900"
      :data-test-id="questionListTestId"
    >
      <li
        v-for="(question, index) in screeningDetails.questions"
        :key="`${index}-${question}`"
        class="grid grid-cols-[1.5rem_1fr] gap-x-2"
      >
        <span v-if="isOrderedQuestionList" class="pt-px text-shade-800">{{ index + 1 }}.</span>
        <span v-else class="mt-[9px] h-1 w-1 justify-self-center rounded-full bg-shade-800"></span>
        <span>{{ question }}</span>
      </li>
    </component>

    <div
      v-if="screeningDetails.url"
      class="mt-4 text-xs leading-5"
      data-test-id="project-screening-url"
    >
      <p class="text-[11px] font-bold uppercase tracking-[0.12em] text-shade-800">
        Careers page URL
      </p>
      <a
        :href="screeningDetails.urlHref || screeningDetails.url"
        target="_blank"
        rel="noopener noreferrer"
        class="mt-1 block break-all font-medium text-highlight-500 underline underline-offset-4"
      >
        {{ screeningDetails.url }}
      </a>
    </div>

    <p
      v-if="screeningDetails.emptyState"
      class="mt-4 text-xs italic leading-5 text-shade-800"
      data-test-id="project-screening-empty-state"
    >
      {{ screeningDetails.emptyState }}
    </p>

    <p
      v-if="screeningDetails.note"
      class="mt-4 text-xs italic leading-5 text-shade-800"
      data-test-id="project-screening-note"
    >
      {{ screeningDetails.note }}
    </p>
  </div>
</template>
